import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Spinner } from '@/shared/ui/Spinner'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { cn } from '@/shared/utils'
import {
  useAllInOneBundles, useSaveAllInOneBundle, useUpdateAllInOneBundle, useDeleteAllInOneBundle,
  useSaveAllInOneVariant, useDeleteAllInOneVariant,
  useSaveAllInOneItem, useDeleteAllInOneItem,
  useCities,
} from '@/api/hooks'
import type { AllInOneBundle, AllInOneVariant, AllInOneItem } from '@/api/services/allInOneService'
import {
  Plus, Edit, Trash2, Package, Layers, AlertTriangle, X, Search,
  MapPin, ImageIcon, ArrowUpDown, Sparkles, CheckCircle2, Truck, CreditCard, Calendar,
} from 'lucide-react'

// ══════════════════════════════════════════════════════════════════════════
// Small local form pieces (file-local, matches the convention used across
// this codebase rather than a shared form-input system)
// ══════════════════════════════════════════════════════════════════════════
function Field({ label, className, ...props }: { label?: string; className?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <input {...props}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
    </div>
  )
}
function TextareaField({ label, className, ...props }: { label?: string; className?: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <textarea {...props} rows={props.rows ?? 2}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"/>
    </div>
  )
}
function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={cn('relative inline-flex w-10 h-5 rounded-full transition-colors flex-shrink-0', on ? 'bg-brand-500' : 'bg-gray-300')}>
      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', on && 'translate-x-5')}/>
    </button>
  )
}
function SelectField({ label, className, children, ...props }: { label?: string; className?: string; children: React.ReactNode } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <select {...props}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
        {children}
      </select>
    </div>
  )
}
// Chip-list input for `highlights` — type a value, press Enter or click +,
// remove with the x. Mirrors the date-chip pattern used elsewhere in this app.
function TagListField({ label, values, onChange, dir }: { label?: string; values: string[]; onChange: (v: string[]) => void; dir?: 'rtl' | 'ltr' }) {
  const [input, setInput] = useState('')
  const add = () => {
    const v = input.trim()
    if (!v) return
    onChange([...values, v])
    setInput('')
  }
  return (
    <div>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <div className="flex items-center gap-2 mb-1.5">
        <input dir={dir} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
        <button type="button" onClick={add} disabled={!input.trim()}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-white disabled:opacity-40" style={{ background: '#f68b2c' }}>
          <Plus size={15}/>
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v, i) => (
            <span key={i} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold">
              {v}
              <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="text-brand-400 hover:text-red-500"><X size={11}/></button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
function cityLabel(c: any): string {
  return c?.city_translations?.find((t: any) => t?.locale?.toLowerCase() === 'en')?.name
    ?? c?.city_translations?.[0]?.name ?? c?.name ?? `#${c?.id}`
}
function fmtDT(v?: string | null) {
  if (!v) return '—'
  const d = new Date(String(v).replace(' ', 'T'))
  if (isNaN(d.getTime())) return v
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// One switcher governs every translated field in a modal at once, instead of
// repeating EN/AR/UR columns for every single field — a green dot marks a
// language that already has content so nothing gets silently left empty.
type Lang = 'en' | 'ar' | 'ur'
const LANGS: { key: Lang; label: string }[] = [{ key: 'en', label: 'EN' }, { key: 'ar', label: 'AR' }, { key: 'ur', label: 'UR' }]
function LangTabs({ active, onChange, filled }: { active: Lang; onChange: (l: Lang) => void; filled: Record<Lang, boolean> }) {
  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 flex-shrink-0">
      {LANGS.map(l => (
        <button key={l.key} type="button" onClick={() => onChange(l.key)}
          className={cn('relative px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors',
            active === l.key ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-400 hover:text-gray-600')}>
          {l.label}
          {filled[l.key] && (
            <span className="absolute -top-0.5 -end-0.5 w-1.5 h-1.5 rounded-full bg-emerald-500 ring-2 ring-white"/>
          )}
        </button>
      ))}
    </div>
  )
}
function suffixOf(lang: Lang) { return lang === 'en' ? '' : `_${lang}` }

// ══════════════════════════════════════════════════════════════════════════
// Bundle modal
// ══════════════════════════════════════════════════════════════════════════
interface BundleForm {
  title: string; subtitle: string; title_ar: string; title_ur: string
  subtitle_ar: string; subtitle_ur: string
  badge: string; badge_ar: string; badge_ur: string
  highlights: string[]; highlights_ar: string[]; highlights_ur: string[]
  image_url: string; city_id: string; sort_order: string; is_active: boolean
}
const BLANK_BUNDLE: BundleForm = {
  title: '', subtitle: '', title_ar: '', title_ur: '', subtitle_ar: '', subtitle_ur: '',
  badge: '', badge_ar: '', badge_ur: '',
  highlights: [], highlights_ar: [], highlights_ur: [],
  image_url: '', city_id: '', sort_order: '', is_active: true,
}

function BundleModal({ open, onClose, editing }: { open: boolean; onClose: () => void; editing: AllInOneBundle | null }) {
  const { t } = useTranslation()
  const [form, setForm] = useState<BundleForm>(BLANK_BUNDLE)
  const [lang, setLang] = useState<Lang>('en')
  const create = useSaveAllInOneBundle()
  const update = useUpdateAllInOneBundle()
  const saving = create.isPending || update.isPending
  const { data: citiesData } = useCities({ limit: 500 })
  const cities: any[] = citiesData?.data ?? []

  const [seededFor, setSeededFor] = useState<number | 'new' | null>(null)
  const key = editing ? editing.id : (open ? 'new' : null)
  if (open && key !== seededFor) {
    setSeededFor(key)
    setLang('en')
    const e: any = editing
    setForm(editing ? {
      title: e.title ?? '', subtitle: e.subtitle ?? '',
      title_ar: e.title_ar ?? '', title_ur: e.title_ur ?? '',
      subtitle_ar: e.subtitle_ar ?? '', subtitle_ur: e.subtitle_ur ?? '',
      badge: e.badge ?? '', badge_ar: e.badge_ar ?? '', badge_ur: e.badge_ur ?? '',
      highlights: Array.isArray(e.highlights) ? e.highlights : [],
      highlights_ar: Array.isArray(e.highlights_ar) ? e.highlights_ar : [],
      highlights_ur: Array.isArray(e.highlights_ur) ? e.highlights_ur : [],
      image_url: e.image_url ?? '', city_id: e.city_id != null ? String(e.city_id) : '',
      sort_order: e.sort_order != null ? String(e.sort_order) : '',
      is_active: !!e.is_active,
    } : BLANK_BUNDLE)
  }

  const sfx = suffixOf(lang)
  const setLocal = (field: 'title' | 'subtitle' | 'badge' | 'highlights', v: any) =>
    setForm(f => ({ ...f, [`${field}${sfx}`]: v }))

  const submit = async () => {
    if (!form.title.trim()) { toast.error(t('allInOnePackage.errTitleRequired')); return }
    try {
      const data = {
        title: form.title, subtitle: form.subtitle,
        title_ar: form.title_ar, title_ur: form.title_ur,
        subtitle_ar: form.subtitle_ar, subtitle_ur: form.subtitle_ur,
        badge: form.badge, badge_ar: form.badge_ar, badge_ur: form.badge_ur,
        highlights: form.highlights, highlights_ar: form.highlights_ar, highlights_ur: form.highlights_ur,
        image_url: form.image_url,
        city_id: form.city_id ? Number(form.city_id) : null,
        sort_order: form.sort_order ? Number(form.sort_order) : 0,
        is_active: form.is_active,
      }
      if (editing) await update.mutateAsync({ id: editing.id, ...data })
      else await create.mutateAsync(data)
      toast.success(editing ? t('allInOnePackage.bundleUpdated') : t('allInOnePackage.bundleCreated'))
      onClose()
    } catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Modal open={open} onClose={onClose} title={editing ? t('allInOnePackage.editBundle') : t('allInOnePackage.addBundle')} size="md">
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('allInOnePackage.translatedFields')}</span>
            <LangTabs active={lang} onChange={setLang}
              filled={{ en: !!form.title, ar: !!form.title_ar, ur: !!form.title_ur }}/>
          </div>
          <Field label={t('allInOnePackage.fieldTitle')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`title${sfx}`]} onChange={e => setLocal('title', e.target.value)}/>
          <TextareaField label={t('allInOnePackage.fieldSubtitle')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`subtitle${sfx}`]} onChange={e => setLocal('subtitle', e.target.value)}/>
          <Field label={t('allInOnePackage.badge')} placeholder="Best value" dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`badge${sfx}`]} onChange={e => setLocal('badge', e.target.value)}/>
          <TagListField label={t('allInOnePackage.highlights')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            values={(form as any)[`highlights${sfx}`]} onChange={v => setLocal('highlights', v)}/>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('allInOnePackage.imageUrl')} placeholder="https://..." value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}/>
          <Field label={t('allInOnePackage.sortOrder')} type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}/>
        </div>
        <SelectField label={t('allInOnePackage.city')} value={form.city_id} onChange={e => setForm(f => ({ ...f, city_id: e.target.value }))}>
          <option value="">{t('allInOnePackage.allCities')}</option>
          {cities.map((c: any) => <option key={c.id} value={c.id}>{cityLabel(c)}</option>)}
        </SelectField>
        <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-100 bg-gray-50">
          <span className="text-sm font-medium text-gray-700">{t('allInOnePackage.active')}</span>
          <Switch on={form.is_active} onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}/>
        </div>
        <Button className="w-full justify-center" loading={saving} onClick={submit}>
          {editing ? t('common.saveChanges') : t('allInOnePackage.create')}
        </Button>
      </div>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Variant modal
// ══════════════════════════════════════════════════════════════════════════
interface VariantForm {
  code: string; has_licence: boolean
  title: string; description: string
  title_ar: string; title_ur: string; description_ar: string; description_ur: string
  price: string; offer_price: string; currency: string; total_sessions: string
  is_pickup_included: boolean; tabby_enabled: boolean
  sort_order: string; is_active: boolean
}
const BLANK_VARIANT: VariantForm = {
  code: '', has_licence: false,
  title: '', description: '', title_ar: '', title_ur: '', description_ar: '', description_ur: '',
  price: '', offer_price: '', currency: 'AED', total_sessions: '',
  is_pickup_included: true, tabby_enabled: true,
  sort_order: '', is_active: true,
}

function VariantModal({ open, onClose, bundleId, editing }: {
  open: boolean; onClose: () => void; bundleId: number | null; editing: AllInOneVariant | null
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<VariantForm>(BLANK_VARIANT)
  const [lang, setLang] = useState<Lang>('en')
  const save = useSaveAllInOneVariant()

  const [seededFor, setSeededFor] = useState<number | 'new' | null>(null)
  const key = editing ? editing.id : (open ? 'new' : null)
  if (open && key !== seededFor) {
    setSeededFor(key)
    setLang('en')
    const e: any = editing
    setForm(editing ? {
      code: e.code ?? '', has_licence: !!e.has_licence,
      title: e.title ?? '', description: e.description ?? '',
      title_ar: e.title_ar ?? '', title_ur: e.title_ur ?? '',
      description_ar: e.description_ar ?? '', description_ur: e.description_ur ?? '',
      price: e.price != null ? String(e.price) : '',
      offer_price: e.offer_price != null ? String(e.offer_price) : '',
      currency: e.currency ?? 'AED',
      total_sessions: e.total_sessions != null ? String(e.total_sessions) : '',
      is_pickup_included: e.is_pickup_included !== false, tabby_enabled: e.tabby_enabled !== false,
      sort_order: e.sort_order != null ? String(e.sort_order) : '',
      is_active: !!e.is_active,
    } : BLANK_VARIANT)
  }

  const sfx = suffixOf(lang)
  const setLocal = (field: 'title' | 'description', v: string) => setForm(f => ({ ...f, [`${field}${sfx}`]: v }))

  const submit = async () => {
    if (!form.code.trim() || !form.title.trim()) { toast.error(t('allInOnePackage.errCodeTitleRequired')); return }
    if (!editing && !form.price.trim()) { toast.error(t('allInOnePackage.errPriceRequired')); return }
    try {
      await save.mutateAsync({
        ...(editing ? { id: editing.id } : { bundle_id: bundleId }),
        code: form.code.trim(), has_licence: form.has_licence,
        title: form.title, description: form.description,
        title_ar: form.title_ar, title_ur: form.title_ur,
        description_ar: form.description_ar, description_ur: form.description_ur,
        price: form.price ? Number(form.price) : undefined,
        offer_price: form.offer_price ? Number(form.offer_price) : undefined,
        currency: form.currency || 'AED',
        total_sessions: form.total_sessions ? Number(form.total_sessions) : undefined,
        is_pickup_included: form.is_pickup_included, tabby_enabled: form.tabby_enabled,
        sort_order: form.sort_order ? Number(form.sort_order) : 0,
        is_active: form.is_active,
      })
      toast.success(editing ? t('allInOnePackage.variantUpdated') : t('allInOnePackage.variantCreated'))
      onClose()
    } catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Modal open={open} onClose={onClose} title={editing ? t('allInOnePackage.editVariant') : t('allInOnePackage.addVariant')} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('allInOnePackage.code')} placeholder="e.g. scratch" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}/>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">{t('allInOnePackage.hasLicence')}</label>
            <div className="flex items-center h-9">
              <Switch on={form.has_licence} onClick={() => setForm(f => ({ ...f, has_licence: !f.has_licence }))}/>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t('allInOnePackage.price')} type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}/>
          <Field label={t('allInOnePackage.offerPrice')} type="number" min="0" value={form.offer_price} onChange={e => setForm(f => ({ ...f, offer_price: e.target.value }))}/>
          <Field label={t('allInOnePackage.currency')} value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('allInOnePackage.totalSessions')} type="number" min="0" value={form.total_sessions} onChange={e => setForm(f => ({ ...f, total_sessions: e.target.value }))}/>
          <Field label={t('allInOnePackage.sortOrder')} type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}/>
        </div>

        <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('allInOnePackage.translatedFields')}</span>
            <LangTabs active={lang} onChange={setLang}
              filled={{ en: !!form.title, ar: !!form.title_ar, ur: !!form.title_ur }}/>
          </div>
          <Field label={t('allInOnePackage.fieldTitle')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`title${sfx}`]} onChange={e => setLocal('title', e.target.value)}/>
          <TextareaField label={t('allInOnePackage.description')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`description${sfx}`]} onChange={e => setLocal('description', e.target.value)}/>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-100 bg-gray-50">
            <span className="text-sm font-medium text-gray-700">{t('allInOnePackage.pickupIncluded')}</span>
            <Switch on={form.is_pickup_included} onClick={() => setForm(f => ({ ...f, is_pickup_included: !f.is_pickup_included }))}/>
          </div>
          <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-100 bg-gray-50">
            <span className="text-sm font-medium text-gray-700">{t('allInOnePackage.tabbyEnabled')}</span>
            <Switch on={form.tabby_enabled} onClick={() => setForm(f => ({ ...f, tabby_enabled: !f.tabby_enabled }))}/>
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-100 bg-gray-50">
          <span className="text-sm font-medium text-gray-700">{t('allInOnePackage.active')}</span>
          <Switch on={form.is_active} onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}/>
        </div>
        <Button className="w-full justify-center" loading={save.isPending} onClick={submit}>
          {editing ? t('common.saveChanges') : t('allInOnePackage.create')}
        </Button>
      </div>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Item modal
// ══════════════════════════════════════════════════════════════════════════
interface ItemForm {
  code: string; item_type: string; sessions_count: string; icon: string
  title: string; title_ar: string; title_ur: string
  subtitle: string; subtitle_ar: string; subtitle_ur: string; sort_order: string
}
const BLANK_ITEM: ItemForm = {
  code: '', item_type: '', sessions_count: '', icon: '',
  title: '', title_ar: '', title_ur: '', subtitle: '', subtitle_ar: '', subtitle_ur: '', sort_order: '',
}

function ItemModal({ open, onClose, variantId, editing }: {
  open: boolean; onClose: () => void; variantId: number | null; editing: AllInOneItem | null
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<ItemForm>(BLANK_ITEM)
  const [lang, setLang] = useState<Lang>('en')
  const save = useSaveAllInOneItem()

  const [seededFor, setSeededFor] = useState<number | 'new' | null>(null)
  const key = editing ? editing.id : (open ? 'new' : null)
  if (open && key !== seededFor) {
    setSeededFor(key)
    setLang('en')
    setForm(editing ? {
      code: editing.code ?? '', item_type: editing.item_type ?? '',
      sessions_count: editing.sessions_count != null ? String(editing.sessions_count) : '',
      icon: editing.icon ?? '', title: editing.title ?? '',
      title_ar: editing.title_ar ?? '', title_ur: editing.title_ur ?? '',
      subtitle: editing.subtitle ?? '', subtitle_ar: editing.subtitle_ar ?? '', subtitle_ur: editing.subtitle_ur ?? '',
      sort_order: editing.sort_order != null ? String(editing.sort_order) : '',
    } : BLANK_ITEM)
  }

  const sfx = suffixOf(lang)
  const setLocal = (field: 'title' | 'subtitle', v: string) => setForm(f => ({ ...f, [`${field}${sfx}`]: v }))

  const submit = async () => {
    if (!form.code.trim() || !form.title.trim()) { toast.error(t('allInOnePackage.errCodeTitleRequired')); return }
    try {
      await save.mutateAsync({
        ...(editing ? { id: editing.id } : { variant_id: variantId }),
        code: form.code.trim(), item_type: form.item_type || undefined,
        sessions_count: form.sessions_count ? Number(form.sessions_count) : undefined,
        icon: form.icon || undefined,
        title: form.title, title_ar: form.title_ar, title_ur: form.title_ur,
        subtitle: form.subtitle, subtitle_ar: form.subtitle_ar, subtitle_ur: form.subtitle_ur,
        sort_order: form.sort_order ? Number(form.sort_order) : undefined,
      })
      toast.success(editing ? t('allInOnePackage.itemUpdated') : t('allInOnePackage.itemCreated'))
      onClose()
    } catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Modal open={open} onClose={onClose} title={editing ? t('allInOnePackage.editItem') : t('allInOnePackage.addItem')} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <Field className="col-span-2" label={t('allInOnePackage.code')} placeholder="e.g. practical" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}/>
          <Field label={t('allInOnePackage.itemType')} placeholder="sessions" value={form.item_type} onChange={e => setForm(f => ({ ...f, item_type: e.target.value }))}/>
          <Field label={t('allInOnePackage.icon')} placeholder="car" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('allInOnePackage.sessionsCount')} type="number" min="0" value={form.sessions_count} onChange={e => setForm(f => ({ ...f, sessions_count: e.target.value }))}/>
          <Field label={t('allInOnePackage.sortOrder')} type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}/>
        </div>

        <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('allInOnePackage.translatedFields')}</span>
            <LangTabs active={lang} onChange={setLang}
              filled={{ en: !!form.title, ar: !!form.title_ar, ur: !!form.title_ur }}/>
          </div>
          <Field label={t('allInOnePackage.fieldTitle')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`title${sfx}`]} onChange={e => setLocal('title', e.target.value)}/>
          <TextareaField label={t('allInOnePackage.fieldSubtitle')} dir={lang === 'en' ? 'ltr' : 'rtl'}
            value={(form as any)[`subtitle${sfx}`]} onChange={e => setLocal('subtitle', e.target.value)}/>
        </div>

        <Button className="w-full justify-center" loading={save.isPending} onClick={submit}>
          {editing ? t('common.saveChanges') : t('allInOnePackage.create')}
        </Button>
      </div>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Item row — a numbered checklist entry, matching the app's own "01 File
// opening / 02 Eye test..." layout so admins recognise what they're building.
// ══════════════════════════════════════════════════════════════════════════
function ItemRow({ index, item, onEdit, onDelete }: { index: number; item: AllInOneItem; onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
      <span className="w-6 h-6 rounded-md bg-white border border-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-400 flex-shrink-0">
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-800 truncate">{item.title}</div>
        {(item.item_type || item.sessions_count) && (
          <div className="text-[11px] text-gray-400 truncate">
            {item.item_type}{item.sessions_count ? ` · ${t('allInOnePackage.sessionsCountShort', { count: item.sessions_count })}` : ''}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-brand-600 hover:bg-brand-50"><Edit size={12}/></button>
        <button onClick={onDelete} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50"><Trash2 size={12}/></button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Variant card — a pricing-card style block, shown side by side with its
// sibling options so an admin can compare them at a glance (mirrors the
// customer app's own "Start from scratch" / "I already have a licence" pair).
// ══════════════════════════════════════════════════════════════════════════
function VariantCard({ variant, onEdit, onDelete, onAddItem, onEditItem, onDeleteItem }: {
  variant: AllInOneVariant
  onEdit: () => void; onDelete: () => void
  onAddItem: () => void; onEditItem: (item: AllInOneItem) => void; onDeleteItem: (item: AllInOneItem) => void
}) {
  const { t } = useTranslation()
  const items = useMemo(() => [...(variant.items ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)), [variant.items])
  // offer_price is the current (discounted) price; price is the full price,
  // struck through only when an offer actually undercuts it.
  const currency = variant.currency || 'AED'
  const displayPrice = variant.offer_price ?? variant.price
  const hasDiscount = variant.offer_price != null && variant.price != null && Number(variant.price) > Number(variant.offer_price)

  return (
    <div className={cn('rounded-2xl border bg-white overflow-hidden flex flex-col', variant.is_active ? 'border-gray-200' : 'border-gray-100 opacity-70')}>
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">
              <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 normal-case font-mono">{variant.code}</code>
              <span className={cn('px-1.5 py-0.5 rounded-full', variant.has_licence ? 'bg-sky-50 text-sky-600' : 'bg-amber-50 text-amber-600')}>
                {variant.has_licence ? t('allInOnePackage.hasLicenceYes') : t('allInOnePackage.hasLicenceNo')}
              </span>
              {!variant.is_active && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t('allInOnePackage.inactive')}</span>}
            </div>
            <div className="font-bold text-gray-900 truncate">{variant.title}</div>
            {variant.description && <div className="text-xs text-gray-400 truncate mt-0.5">{variant.description}</div>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={onEdit} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50"><Edit size={13}/></button>
            <button onClick={onDelete} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"><Trash2 size={13}/></button>
          </div>
        </div>
        {displayPrice != null && (
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="text-xl font-black text-gray-900">{displayPrice} <span className="text-xs font-bold text-gray-400">{currency}</span></span>
            {hasDiscount && <span className="text-xs text-gray-400 line-through">{variant.price} {currency}</span>}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          {variant.total_sessions != null && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-semibold">{t('allInOnePackage.sessionsCountShort', { count: variant.total_sessions })}</span>
          )}
          {variant.is_pickup_included && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-semibold"><Truck size={11}/> {t('allInOnePackage.pickupIncluded')}</span>
          )}
          {variant.tabby_enabled && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 font-semibold"><CreditCard size={11}/> {t('allInOnePackage.tabbyEnabled')}</span>
          )}
        </div>
      </div>

      <div className="p-2 flex-1">
        {items.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3">{t('allInOnePackage.noItems')}</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {items.map((item, i) => (
              <ItemRow key={item.id} index={i} item={item} onEdit={() => onEditItem(item)} onDelete={() => onDeleteItem(item)}/>
            ))}
          </div>
        )}
      </div>
      <div className="p-2 pt-0">
        <button onClick={onAddItem}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-gray-200 text-xs font-semibold text-gray-400 hover:border-brand-300 hover:text-brand-600 transition-colors">
          <Plus size={12}/> {t('allInOnePackage.addItem')}
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Bundle list item (left panel)
// ══════════════════════════════════════════════════════════════════════════
function BundleListItem({ bundle, cityName, selected, onClick }: {
  bundle: AllInOneBundle; cityName: string | null; selected: boolean; onClick: () => void
}) {
  const { t } = useTranslation()
  const variantCount = bundle.variants?.length ?? 0
  const itemCount = (bundle.variants ?? []).reduce((n, v) => n + (v.items?.length ?? 0), 0)
  return (
    <button onClick={onClick}
      className={cn(
        'w-full text-left p-3.5 rounded-xl border transition-all',
        selected ? 'border-brand-300 bg-brand-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      )}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className={cn('font-bold text-sm truncate', selected ? 'text-brand-700' : 'text-gray-800')}>{bundle.title}</span>
        <span className={cn('w-2 h-2 rounded-full flex-shrink-0 mt-1.5', bundle.is_active ? 'bg-emerald-500' : 'bg-gray-300')}
          title={bundle.is_active ? t('allInOnePackage.active') : t('allInOnePackage.inactive')}/>
      </div>
      <div className="flex items-center gap-1 text-[11px] text-gray-400">
        <MapPin size={11} className="flex-shrink-0"/>
        <span className="truncate">{cityName ?? t('allInOnePackage.allCities')}</span>
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-400">
        <span>{t('allInOnePackage.variantAndItemCount', { variants: variantCount, items: itemCount })}</span>
        {bundle.created_at && (
          <span className="flex items-center gap-1 flex-shrink-0"><Calendar size={10}/>{fmtDT(bundle.created_at)}</span>
        )}
      </div>
    </button>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Bundle preview (right panel header) — a dark card echoing what the
// customer app actually shows, so an admin gets a WYSIWYG-ish sanity check.
// ══════════════════════════════════════════════════════════════════════════
function BundlePreview({ bundle, cityName, onEdit, onDelete, onAddVariant }: {
  bundle: AllInOneBundle; cityName: string | null
  onEdit: () => void; onDelete: () => void; onAddVariant: () => void
}) {
  const { t } = useTranslation()
  const highlights = bundle.highlights ?? []
  return (
    <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
      <div className="p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1c2b22, #0f1a14)' }}>
        {bundle.image_url && (
          <img src={bundle.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20"/>
        )}
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            {bundle.badge && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: '#f68b2c' }}>
                <Sparkles size={11}/> {bundle.badge}
              </span>
            )}
            {!bundle.is_active && (
              <span className="px-2.5 py-1 rounded-full bg-white/15 text-[11px] font-bold">{t('allInOnePackage.inactive')}</span>
            )}
          </div>
          <h2 className="text-2xl font-black mb-1">{bundle.title}</h2>
          {bundle.subtitle && <p className="text-sm text-white/70 mb-4">{bundle.subtitle}</p>}
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {highlights.map((h, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-xs font-medium">
                  <CheckCircle2 size={12} style={{ color: '#f68b2c' }}/> {h}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between px-5 py-3 bg-white flex-wrap gap-2">
        <div className="flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><MapPin size={12}/> {cityName ?? t('allInOnePackage.allCities')}</span>
          <span className="flex items-center gap-1"><ArrowUpDown size={12}/> {t('allInOnePackage.sortOrder')} {bundle.sort_order ?? 0}</span>
          {bundle.created_at && <span className="flex items-center gap-1"><Calendar size={12}/> {fmtDT(bundle.created_at)}</span>}
          {bundle.image_url && <span className="flex items-center gap-1"><ImageIcon size={12}/> {t('allInOnePackage.imageUrl')}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" icon={<Edit size={13}/>} onClick={onEdit}>{t('common.edit')}</Button>
          <button onClick={onDelete} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 bg-red-50 hover:bg-red-100"><Trash2 size={14}/></button>
          <Button size="sm" icon={<Plus size={13}/>} onClick={onAddVariant}>{t('allInOnePackage.addVariant')}</Button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Main page — master-detail: pick a bundle on the left, manage its options
// and items in a focused view on the right.
// ══════════════════════════════════════════════════════════════════════════
export function AllInOnePackagePage() {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useAllInOneBundles()
  const bundles: AllInOneBundle[] = data?.data ?? []
  const { data: citiesData } = useCities({ limit: 500 })
  const cities: any[] = citiesData?.data ?? []

  const deleteBundle = useDeleteAllInOneBundle()
  const deleteVariant = useDeleteAllInOneVariant()
  const deleteItem = useDeleteAllInOneItem()

  const [search, setSearch] = useState('')
  const filteredBundles = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return bundles
    return bundles.filter(b => (b.title ?? '').toLowerCase().includes(q) || (b.badge ?? '').toLowerCase().includes(q))
  }, [bundles, search])

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const selectedBundle = bundles.find(b => b.id === selectedId) ?? filteredBundles[0] ?? null
  const selectedCityName = selectedBundle?.city_id
    ? cityLabel(cities.find((c: any) => c.id === selectedBundle.city_id))
    : null

  const [bundleModal, setBundleModal] = useState<{ open: boolean; editing: AllInOneBundle | null }>({ open: false, editing: null })
  const [variantModal, setVariantModal] = useState<{ open: boolean; bundleId: number | null; editing: AllInOneVariant | null }>({ open: false, bundleId: null, editing: null })
  const [itemModal, setItemModal] = useState<{ open: boolean; variantId: number | null; editing: AllInOneItem | null }>({ open: false, variantId: null, editing: null })

  const [bundleToDelete, setBundleToDelete] = useState<AllInOneBundle | null>(null)
  const [variantToDelete, setVariantToDelete] = useState<AllInOneVariant | null>(null)
  const [itemToDelete, setItemToDelete] = useState<AllInOneItem | null>(null)

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('allInOnePackage.title')}
        subtitle={t('allInOnePackage.subtitle')}
        breadcrumbs={[{ label: t('allInOnePackage.breadcrumbDashboard'), to: '/dashboard' }, { label: t('allInOnePackage.title') }]}
        action={
          <Button icon={<Plus size={14}/>} size="sm" onClick={() => setBundleModal({ open: true, editing: null })}>
            {t('allInOnePackage.addBundle')}
          </Button>
        }
      />

      {isError && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          <AlertTriangle size={16} className="flex-shrink-0"/> {t('allInOnePackage.loadError')}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner/></div>
      ) : bundles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <EmptyState icon={<Package size={22}/>} message={t('allInOnePackage.noBundles')} description={t('allInOnePackage.noBundlesHint')}/>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5 items-start">
          {/* Left: bundle list */}
          <div className="space-y-3">
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t('allInOnePackage.searchBundles')}
                className="w-full h-9 ps-9 pe-3 text-sm text-gray-700 placeholder-gray-400 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400/30"/>
            </div>
            <div className="space-y-2">
              {filteredBundles.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">{t('allInOnePackage.noSearchResults')}</p>
              ) : filteredBundles.map(bundle => (
                <BundleListItem key={bundle.id} bundle={bundle}
                  cityName={bundle.city_id ? cityLabel(cities.find((c: any) => c.id === bundle.city_id)) : null}
                  selected={selectedBundle?.id === bundle.id} onClick={() => setSelectedId(bundle.id)}/>
              ))}
            </div>
          </div>

          {/* Right: selected bundle detail */}
          {selectedBundle ? (
            <div className="space-y-5 min-w-0">
              <BundlePreview
                bundle={selectedBundle}
                cityName={selectedCityName}
                onEdit={() => setBundleModal({ open: true, editing: selectedBundle })}
                onDelete={() => setBundleToDelete(selectedBundle)}
                onAddVariant={() => setVariantModal({ open: true, bundleId: selectedBundle.id, editing: null })}
              />

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Layers size={14} className="text-gray-400"/>
                  <h3 className="text-sm font-bold text-gray-700">{t('allInOnePackage.optionsHeading')}</h3>
                </div>
                {(selectedBundle.variants ?? []).length === 0 ? (
                  <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-10 text-center">
                    <p className="text-sm text-gray-400 mb-3">{t('allInOnePackage.noVariants')}</p>
                    <Button size="sm" icon={<Plus size={13}/>} onClick={() => setVariantModal({ open: true, bundleId: selectedBundle.id, editing: null })}>
                      {t('allInOnePackage.addVariant')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {(selectedBundle.variants ?? []).map(variant => (
                      <VariantCard key={variant.id} variant={variant}
                        onEdit={() => setVariantModal({ open: true, bundleId: selectedBundle.id, editing: variant })}
                        onDelete={() => setVariantToDelete(variant)}
                        onAddItem={() => setItemModal({ open: true, variantId: variant.id, editing: null })}
                        onEditItem={(item) => setItemModal({ open: true, variantId: variant.id, editing: item })}
                        onDeleteItem={(item) => setItemToDelete(item)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <EmptyState icon={<Package size={22}/>} message={t('allInOnePackage.selectBundle')}/>
            </div>
          )}
        </div>
      )}

      <BundleModal open={bundleModal.open} onClose={() => setBundleModal({ open: false, editing: null })} editing={bundleModal.editing}/>
      <VariantModal open={variantModal.open} onClose={() => setVariantModal({ open: false, bundleId: null, editing: null })}
        bundleId={variantModal.bundleId} editing={variantModal.editing}/>
      <ItemModal open={itemModal.open} onClose={() => setItemModal({ open: false, variantId: null, editing: null })}
        variantId={itemModal.variantId} editing={itemModal.editing}/>

      <ConfirmDialog
        open={!!bundleToDelete} onClose={() => setBundleToDelete(null)}
        onConfirm={async () => {
          if (!bundleToDelete) return
          try {
            await deleteBundle.mutateAsync(bundleToDelete.id)
            toast.success(t('allInOnePackage.bundleDeleted'))
            if (selectedId === bundleToDelete.id) setSelectedId(null)
          }
          catch (e) { toast.error(getApiError(e)) }
          finally { setBundleToDelete(null) }
        }}
        title={t('allInOnePackage.deleteBundleTitle')}
        description={t('allInOnePackage.deleteBundleConfirm', { title: bundleToDelete?.title })}
        loading={deleteBundle.isPending}
      />
      <ConfirmDialog
        open={!!variantToDelete} onClose={() => setVariantToDelete(null)}
        onConfirm={async () => {
          if (!variantToDelete) return
          try { await deleteVariant.mutateAsync(variantToDelete.id); toast.success(t('allInOnePackage.variantDeleted')) }
          catch (e) { toast.error(getApiError(e)) }
          finally { setVariantToDelete(null) }
        }}
        title={t('allInOnePackage.deleteVariantTitle')}
        description={t('allInOnePackage.deleteVariantConfirm', { title: variantToDelete?.title })}
        loading={deleteVariant.isPending}
      />
      <ConfirmDialog
        open={!!itemToDelete} onClose={() => setItemToDelete(null)}
        onConfirm={async () => {
          if (!itemToDelete) return
          try { await deleteItem.mutateAsync(itemToDelete.id); toast.success(t('allInOnePackage.itemDeleted')) }
          catch (e) { toast.error(getApiError(e)) }
          finally { setItemToDelete(null) }
        }}
        title={t('allInOnePackage.deleteItemTitle')}
        description={t('allInOnePackage.deleteItemConfirm', { title: itemToDelete?.title })}
        loading={deleteItem.isPending}
      />
    </div>
  )
}
